// Research Collection Storage Utilities
// Manages saving, loading, and sharing research collections

import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const STORAGE_KEY = 'gematria_research_collection';
const MAX_ENTRIES = 100; // Limit to prevent storage issues

// Platform-specific storage wrapper
const storage = {
  getItem: async (key) => {
    if (Platform.OS === 'web') {
      return localStorage.getItem(key);
    }
    return await AsyncStorage.getItem(key);
  },
  setItem: async (key, value) => {
    if (Platform.OS === 'web') {
      localStorage.setItem(key, value);
    } else {
      await AsyncStorage.setItem(key, value);
    }
  },
  removeItem: async (key) => {
    if (Platform.OS === 'web') {
      localStorage.removeItem(key);
    } else {
      await AsyncStorage.removeItem(key);
    }
  }
};

const RESEARCH_SOURCES = {
  calculator: { key: 'calculator', label: 'Gematria Calculator', icon: '🔢' },
  compare: { key: 'compare', label: 'Compare', icon: '🔄' },
  number: { key: 'number', label: 'Number Properties', icon: '🔍' },
  'date-calculator': { key: 'date-calculator', label: 'Date Calculator', icon: '📅' },
  'bible-match': { key: 'bible-match', label: 'Bible Match', icon: '📖' },
};

const getResearchSourceKey = (entry = {}) => {
  if (entry.source && RESEARCH_SOURCES[entry.source]) return entry.source;

  const tags = Array.isArray(entry.tags) ? entry.tags : [];
  if (tags.includes('compare')) return 'compare';
  if (tags.includes('bible-match')) return 'bible-match';
  if (entry.type === 'date' || entry.startDate != null || entry.endDate != null || tags.includes('date')) {
    return 'date-calculator';
  }
  if (entry.type === 'number' || tags.includes('number')) return 'number';
  return 'calculator';
};

export const getResearchSourceInfo = (entry = {}) => (
  RESEARCH_SOURCES[getResearchSourceKey(entry)] || RESEARCH_SOURCES.calculator
);

/**
 * Research Entry Structure:
 * {
 *   id: string (timestamp-based)
 *   text: string
 *   timestamp: number
 *   note: string (optional user note)
 *   selectedCiphers: object
 *   results: array (cached results)
 *   tags: array (optional tags like "biblical", "names", etc)
 * }
 */

/**
 * Save a calculation to research collection
 */
export const saveToResearch = async (text, selectedCiphers, results, note = '', tags = [], source = null) => {
  const research = await getResearchCollection();

  // Check if we've hit the limit
  if (research.length >= MAX_ENTRIES) {
    throw new Error(`Research list is full! Maximum ${MAX_ENTRIES} entries allowed. Please delete some entries before adding more.`);
  }

  const entry = {
    id: Date.now().toString(),
    type: 'word',
    source: getResearchSourceKey({ source, tags, type: 'word' }),
    text: text.trim(),
    timestamp: Date.now(),
    note: note.trim(),
    selectedCiphers: selectedCiphers,
    results: results,
    tags: tags,
  };

  research.unshift(entry); // Add to beginning

  try {
    await storage.setItem(STORAGE_KEY, JSON.stringify(research));
  } catch (error) {
    // Handle storage quota exceeded
    if (error.name === 'QuotaExceededError') {
      throw new Error('Storage limit exceeded! Please delete some entries to free up space.');
    }
    throw error;
  }

  return entry;
};

/**
 * Save a date calculation to research collection
 */
export const saveDateToResearch = async (text, startDate, endDate, includeEndDate, results, note = '', tags = ['date'], source = 'date-calculator') => {
  const research = await getResearchCollection();

  if (research.length >= MAX_ENTRIES) {
    throw new Error(`Research list is full! Maximum ${MAX_ENTRIES} entries allowed.`);
  }

  const entry = {
    id: Date.now().toString(),
    type: 'date',
    source: getResearchSourceKey({ source, tags, type: 'date' }),
    text: text.trim(),
    timestamp: Date.now(),
    startDate,
    endDate,
    includeEndDate,
    results, // Storing the full duration object
    note: note.trim(),
    tags: tags,
  };

  research.unshift(entry);

  try {
    await storage.setItem(STORAGE_KEY, JSON.stringify(research));
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      throw new Error('Storage limit exceeded!');
    }
    throw error;
  }

  return entry;
};

/**
 * Get all research entries
 */
export const getResearchCollection = async () => {
  try {
    const stored = await storage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading research collection:', error);
    return [];
  }
};

/**
 * Delete a research entry
 */
export const deleteResearchEntry = async (id) => {
  const research = await getResearchCollection();
  const filtered = research.filter(entry => entry.id !== id);
  await storage.setItem(STORAGE_KEY, JSON.stringify(filtered));
  return filtered;
};

/**
 * Update a research entry
 */
export const updateResearchEntry = async (id, updates) => {
  const research = await getResearchCollection();
  const index = research.findIndex(entry => entry.id === id);

  if (index !== -1) {
    research[index] = { ...research[index], ...updates };
    await storage.setItem(STORAGE_KEY, JSON.stringify(research));
    return research[index];
  }

  return null;
};

/**
 * Export research collection as JSON
 */
export const exportResearchCollection = async () => {
  const research = await getResearchCollection();
  const dataStr = JSON.stringify(research, null, 2);

  if (Platform.OS === 'web') {
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `gematria-research-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  } else {
    // For mobile, return the data string for sharing
    return dataStr;
  }
};

/**
 * Import research collection from JSON
 */
export const importResearchCollection = async (jsonData) => {
  try {
    const imported = JSON.parse(jsonData);
    if (Array.isArray(imported)) {
      const existing = await getResearchCollection();
      const merged = [...imported, ...existing];
      await storage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return merged;
    }
    return null;
  } catch (error) {
    console.error('Error importing research:', error);
    return null;
  }
};

// Custom Base64 encoder for environments where btoa is not available
const base64Encode = (str) => {
  try {
    if (typeof btoa === 'function') return btoa(str);
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    for (let block, charCode, idx = 0, map = chars; str.charAt(idx | 0) || (map = '=', idx % 1); output += map.charAt(63 & block >> 8 - idx % 1 * 8)) {
      charCode = str.charCodeAt(idx += 3 / 4);
      if (charCode > 0xFF) throw new Error("'btoa' failed");
      block = block << 8 | charCode;
    }
    return output;
  } catch (e) {
    console.error('Base64 encode error:', e);
    return null;
  }
};

const getActiveCipherNames = (selectedCiphers) => {
  if (Array.isArray(selectedCiphers)) return selectedCiphers.filter(name => typeof name === 'string');
  if (!selectedCiphers || typeof selectedCiphers !== 'object') return [];
  return Object.keys(selectedCiphers).filter(key => selectedCiphers[key]);
};

const getEntryType = (entry = {}) => {
  if (entry.type === 'date' || entry.type === 'number' || entry.type === 'compare' || entry.type === 'word') {
    return entry.type;
  }
  const tags = Array.isArray(entry.tags) ? entry.tags : [];
  if (entry.startDate != null || entry.endDate != null || tags.includes('date')) return 'date';
  if (tags.includes('compare')) return 'compare';
  if (tags.includes('number')) return 'number';
  return 'word';
};

const normalizeSharedEntry = (item = {}) => {
  const compact = Object.prototype.hasOwnProperty.call(item, 't');
  const tags = compact ? item.g : item.tags;
  const ciphers = compact ? item.c : (item.ciphers ?? item.selectedCiphers);
  return {
    type: compact ? item.k : getEntryType(item),
    source: compact ? item.o : item.source,
    text: compact ? item.t : item.text,
    timestamp: compact ? item.p : item.timestamp,
    ciphers: getActiveCipherNames(ciphers),
    results: compact ? item.r : item.results,
    note: compact ? item.n : item.note,
    tags: Array.isArray(tags) ? tags : [],
    startDate: compact ? item.s : item.startDate,
    endDate: compact ? item.e : item.endDate,
    includeEndDate: compact ? item.d : item.includeEndDate,
    metadata: compact ? item.m : item.metadata,
  };
};

/**
 * Share an entire research collection using a compact, backwards-compatible schema.
 */
export const shareResearchCollection = (entries) => {
  try {
    if (!Array.isArray(entries) || entries.length === 0) return '';

    const data = {
      y: 'c',
      v: 2,
      e: entries.filter(Boolean).map(entry => ({
        t: entry.text == null ? '' : String(entry.text),
        k: getEntryType(entry),
        o: getResearchSourceKey(entry),
        p: typeof entry.timestamp === 'number' ? entry.timestamp : undefined,
        c: getActiveCipherNames(entry.selectedCiphers),
        r: entry.results ?? null,
        n: typeof entry.note === 'string' ? entry.note : '',
        g: Array.isArray(entry.tags) ? entry.tags : [],
        s: entry.startDate ?? undefined,
        e: entry.endDate ?? undefined,
        d: typeof entry.includeEndDate === 'boolean' ? entry.includeEndDate : undefined,
        m: entry.metadata ?? undefined,
      }))
    };

    const jsonString = JSON.stringify(data);
    const encoded = base64Encode(unescape(encodeURIComponent(jsonString)));
    if (!encoded) return '';
    return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error encoding research collection:', error);
    return '';
  }
};

// Custom Base64 decoder for environments where atob is not available (like React Native)
const base64Decode = (str) => {
  try {
    if (typeof atob === 'function') return atob(str);

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let output = '';
    str = String(str).replace(/=+$/, '');
    if (str.length % 4 === 1) throw new Error('Invalid base64');
    for (let bc = 0, bs, buffer, idx = 0; buffer = str.charAt(idx++); ~buffer && (bs = bc % 4 ? bs * 64 + buffer : buffer, bc++ % 4) ? output += String.fromCharCode(255 & bs >> (-2 * bc & 6)) : 0) {
      buffer = chars.indexOf(buffer);
    }
    return output;
  } catch (e) {
    console.error('Base64 decode error:', e);
    return null;
  }
};

/**
 * Decode shared research collections in both verbose and compact formats.
 */
export const decodeSharedCollection = (encoded) => {
  try {
    if (!encoded) return null;
    let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';

    const decoded = base64Decode(base64);
    if (!decoded) return null;

    const data = JSON.parse(decodeURIComponent(escape(decoded)));
    if (data.type === 'collection' && Array.isArray(data.entries)) {
      return data.entries.map(normalizeSharedEntry);
    }
    if (data.y === 'c' && Array.isArray(data.e)) {
      return data.e.map(normalizeSharedEntry);
    }
    return null;
  } catch (error) {
    console.error('Error decoding collection:', error);
    return null;
  }
};

/**
 * Import a shared collection into local storage efficiently.
 */
export const importSharedCollection = async (entries) => {
  if (!Array.isArray(entries)) return 0;

  const research = await getResearchCollection();
  const existingMap = new Map();
  const makeDedupKey = (entry, cipherNames) => {
    const type = getEntryType(entry);
    const dateKey = type === 'date' ? `${entry.startDate || ''}::${entry.endDate || ''}` : '';
    return `${type}::${entry.text || ''}::${cipherNames.sort().join('|')}::${dateKey}`;
  };

  research.forEach(entry => {
    existingMap.set(makeDedupKey(entry, getActiveCipherNames(entry && entry.selectedCiphers)), true);
  });

  const { calculateGematria } = require('./calculator');
  let importCount = 0;
  const newEntries = [];

  for (const rawItem of entries) {
    if (!rawItem || typeof rawItem !== 'object') continue;
    const item = normalizeSharedEntry(rawItem);
    const text = item.text == null ? '' : String(item.text).trim();
    const type = getEntryType({ ...item, tags: item.tags });
    const cipherSelection = {};
    item.ciphers.forEach(key => { cipherSelection[key] = true; });
    const dedupKey = makeDedupKey({ ...item, type, text }, [...item.ciphers]);

    if (existingMap.has(dedupKey)) continue;

    let results = item.results;
    if (results == null && type !== 'date') {
      results = await Promise.resolve(calculateGematria(text, cipherSelection));
    }

    const entry = {
      id: `${Date.now()}${Math.random().toString(36).slice(2, 11)}`,
      type,
      source: getResearchSourceKey({ ...item, type, tags: item.tags }),
      text,
      timestamp: typeof item.timestamp === 'number' ? item.timestamp : Date.now(),
      note: typeof item.note === 'string' ? item.note.trim() : '',
      selectedCiphers: cipherSelection,
      results: results ?? (type === 'date' ? {} : []),
      tags: item.tags,
    };

    if (type === 'date') {
      entry.startDate = item.startDate ?? null;
      entry.endDate = item.endDate ?? null;
      entry.includeEndDate = Boolean(item.includeEndDate);
    }
    if (item.metadata != null) entry.metadata = item.metadata;

    newEntries.push(entry);
    existingMap.set(dedupKey, true);
    importCount++;
  }

  if (newEntries.length > 0) {
    const updatedResearch = [...newEntries, ...research].slice(0, MAX_ENTRIES);
    await storage.setItem(STORAGE_KEY, JSON.stringify(updatedResearch));
  }

  return importCount;
};

/**
 * Get summary statistics for research collection
 */
export const getResearchStats = async () => {
  const research = await getResearchCollection();

  return {
    totalEntries: research.length,
    maxEntries: MAX_ENTRIES,
    percentFull: Math.round((research.length / MAX_ENTRIES) * 100),
    totalWords: research.reduce((sum, entry) => sum + entry.text.split(/\s+/).length, 0),
    uniqueCiphers: new Set(
      research.flatMap(entry => 
        entry.selectedCiphers ? Object.keys(entry.selectedCiphers).filter(k => entry.selectedCiphers[k]) : []
      )
    ).size,
    tags: [...new Set(research.flatMap(entry => entry.tags || []))],
  }
};
